import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ArrowLeft, Plus, Edit, Save, Trash2, Sparkles, Search, X, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ImageInput from "../components/ImageInput";

const EMPTY = { name: "", slug: "", description: "", cover_url: "", icon: "", cta: "", order: 0, active: true };

export default function AdminSegments() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  // Companies management state (only when editing an existing segment)
  const [segCompanies, setSegCompanies] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);

  const refresh = () => api.get("/segments").then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("NEW"); setSegCompanies([]); setSearchQ(""); setSearchResults([]); };
  const openEdit = (s) => {
    setForm({...EMPTY, ...s});
    setEditing(s.slug);
    setSearchQ(""); setSearchResults([]);
    loadSegCompanies(s.slug);
  };

  const loadSegCompanies = async (slug) => {
    try {
      const r = await api.get(`/segments/${slug}/companies`);
      setSegCompanies(r.data || []);
    } catch { setSegCompanies([]); }
  };

  const searchCompanies = async () => {
    if (!searchQ.trim()) return;
    setSearchBusy(true);
    try {
      const r = await api.get(`/companies?search=${encodeURIComponent(searchQ.trim())}&include_inactive=true`);
      setSearchResults(r.data || []);
    } catch { setSearchResults([]); }
    finally { setSearchBusy(false); }
  };

  const addCompanyToSeg = async (companyId) => {
    try {
      await api.post(`/segments/${editing}/companies/${companyId}`);
      toast.success("Empresa vinculada");
      await loadSegCompanies(editing);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
  };

  const removeCompanyFromSeg = async (companyId) => {
    try {
      await api.delete(`/segments/${editing}/companies/${companyId}`);
      toast.success("Empresa desvinculada");
      await loadSegCompanies(editing);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {...form, order: parseInt(form.order, 10) || 0};
      if (editing === "NEW") await api.post("/segments", payload);
      else await api.patch(`/segments/${editing}`, payload);
      toast.success("Segmento salvo");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
    finally { setBusy(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Excluir este segmento? Empresas vinculadas perderão o vínculo.")) return;
    try { await api.delete(`/segments/${slug}`); toast.success("Excluído"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/admin" className="text-sm text-stone-500 hover:text-stone-700 inline-flex items-center gap-1"><ArrowLeft size={14}/> Voltar</Link>
      <div className="flex items-center justify-between flex-wrap gap-3 mt-2 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 flex items-center gap-2"><Sparkles size={28}/> Segmentos do Descobrir</h1>
          <p className="text-stone-500 text-sm mt-1">Crie agrupamentos temáticos ("Onde Comer Bem", "Onde Morar Bem"…) e vincule empresas a eles no cadastro da empresa.</p>
        </div>
        <Button onClick={openNew} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="seg-new"><Plus size={14} className="mr-1.5"/>Novo segmento</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <Card key={s.slug} className="overflow-hidden rounded-2xl border-stone-200" data-testid={`seg-card-${s.slug}`}>
            <div className="aspect-[16/9] bg-stone-100 relative">
              {s.cover_url ? <img src={s.cover_url} alt={s.name} loading="lazy" className="w-full h-full object-cover"/> :
                <div className="absolute inset-0 flex items-center justify-center text-stone-300 font-display text-3xl">{s.name[0]}</div>}
              {!s.active && <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] font-bold uppercase">Inativo</span>}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display font-bold text-stone-900">{s.cta || s.name}</h3>
                <span className="text-[10px] font-mono text-stone-400">#{s.slug}</span>
              </div>
              <p className="text-xs text-stone-600 mt-1 line-clamp-2">{s.description}</p>
              <div className="flex justify-end gap-1 mt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="rounded-lg" data-testid={`seg-edit-${s.slug}`}><Edit size={12} className="mr-1"/>Editar</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s.slug)} className="text-red-600 hover:bg-red-50 rounded-lg" data-testid={`seg-delete-${s.slug}`}><Trash2 size={12}/></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing === "NEW" ? "Novo segmento" : "Editar segmento"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-xl mt-1" data-testid="seg-form-name"/></div>
              <div><Label>Chamada (CTA mostrada no card)</Label><Input value={form.cta} onChange={(e) => setForm({...form, cta: e.target.value})} placeholder="Onde Comer Bem" className="rounded-xl mt-1" data-testid="seg-form-cta"/></div>
              <div><Label>Slug (URL)</Label><Input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} placeholder="auto-gerado" className="rounded-xl mt-1 font-mono text-sm" data-testid="seg-form-slug"/></div>
              <div><Label>Ícone Lucide (opcional)</Label><Input value={form.icon} onChange={(e) => setForm({...form, icon: e.target.value})} placeholder="Home, UtensilsCrossed…" className="rounded-xl mt-1 font-mono text-sm"/></div>
              <div className="sm:col-span-2"><Label>Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} className="rounded-xl mt-1"/></div>
              <div className="sm:col-span-2"><Label>Imagem de capa (16:9)</Label><div className="mt-1"><ImageInput value={form.cover_url} onChange={(v) => setForm({...form, cover_url: v})} aspect="16/9" testid="seg-cover"/></div></div>
              <div><Label>Ordem</Label><Input type="number" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} className="rounded-xl mt-1"/></div>
              <div className="flex items-center gap-2 mt-6"><Switch checked={form.active} onCheckedChange={(v) => setForm({...form, active: v})} data-testid="seg-active"/><Label>Ativo</Label></div>
            </div>

            {editing !== "NEW" && (
              <div className="mt-6 pt-5 border-t border-stone-200" data-testid="seg-companies-mgmt">
                <h3 className="font-display text-lg font-bold text-stone-900 flex items-center gap-2"><Building2 size={18}/> Empresas associadas <span className="text-xs font-mono text-stone-400">({segCompanies.length})</span></h3>

                {/* Search + Add */}
                <div className="mt-3 flex gap-2">
                  <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchCompanies())} placeholder="Buscar por nome ou CNPJ..." className="rounded-xl flex-1" data-testid="seg-search-input"/>
                  <Button type="button" onClick={searchCompanies} disabled={searchBusy} variant="outline" className="rounded-xl" data-testid="seg-search-btn"><Search size={14} className="mr-1"/>{searchBusy ? "..." : "Buscar"}</Button>
                </div>

                {searchResults.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-stone-200 divide-y divide-stone-100" data-testid="seg-search-results">
                    {searchResults.map((c) => {
                      const isIn = (c.segments || []).includes(editing);
                      return (
                        <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-stone-50">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-stone-900 truncate">{c.name}</div>
                            <div className="text-[11px] text-stone-500 flex gap-2 truncate">
                              <span>{c.city_slug}</span>
                              {c.cnpj && <span className="font-mono">{c.cnpj}</span>}
                              <span className="uppercase">{c.plan}</span>
                            </div>
                          </div>
                          {isIn ? (
                            <span className="text-[11px] text-emerald-700 font-bold uppercase">Já vinculada</span>
                          ) : (
                            <Button size="sm" onClick={() => addCompanyToSeg(c.id)} className="bg-blue-900 hover:bg-blue-950 text-white rounded-lg" data-testid={`seg-add-${c.id}`}><Plus size={12} className="mr-1"/>Adicionar</Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Already-linked companies */}
                <div className="mt-4">
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Vinculadas ao segmento</Label>
                  {segCompanies.length === 0 ? (
                    <p className="text-sm text-stone-400 mt-2 italic">Nenhuma empresa vinculada ainda.</p>
                  ) : (
                    <div className="mt-2 rounded-xl border border-stone-200 divide-y divide-stone-100 max-h-56 overflow-y-auto" data-testid="seg-linked-list">
                      {segCompanies.map((c) => (
                        <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`seg-linked-${c.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-stone-900 truncate">{c.name}</div>
                            <div className="text-[11px] text-stone-500 flex gap-2 truncate">
                              <span>{c.city_slug}</span>
                              {c.cnpj && <span className="font-mono">{c.cnpj}</span>}
                              <span className="uppercase">{c.plan}</span>
                            </div>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => removeCompanyFromSeg(c.id)} className="text-red-600 hover:bg-red-50 rounded-lg" data-testid={`seg-remove-${c.id}`}><X size={14} className="mr-1"/>Retirar</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={save} disabled={busy || !form.name} className="bg-blue-900 hover:bg-blue-950 text-white" data-testid="seg-save">
                <Save size={14} className="mr-1.5"/>{busy ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
